/**
 * Pure decision for whether the loader should clear (hide) the current order.
 *
 * Extracted from the order route's server loader + clientLoader, which both
 * carried this logic inline. Pulling it here removes the duplication and makes
 * the decision testable.
 *
 * IMPORTANT — history:
 *  - We used to clear whenever the order's stored `orderTypeCode` differed from
 *    the one now expected. That wiped the whole cart on a plain Pickup ⇄ Delivery
 *    switch. Sal's keeps the cart and re-prices on a fulfilment change, so we
 *    stopped clearing on a type-code mismatch.
 *  - We then cleared on a *menu-identity* mismatch: any line not on the single
 *    menu being displayed cleared the cart. That was blunt — it couldn't tell a
 *    genuinely stale item from an item on a different-but-still-open menu, so it
 *    wiped multi-menu carts even when every menu was available.
 *
 * Now the decision is *time-based*, mirroring what the backend already enforces
 * at placement (it validates each referenced menu with `IsMenuAvailable` at the
 * requested time — see orderHandler). The cart is valid iff every line's menu is
 * one of THIS order type's menus AND open at the requested time. This lets a cart
 * span multiple concurrently-open menus (the frontend agrees with the backend),
 * while a line on a foreign or time-closed menu still clears.
 */
import { OrderStatus } from "~/entities/orderEntities";
import { MenuShort } from "~/entities/menuEntities";
import { isMenuOpen } from "~/helpers/menuHelpers";

export type OrderClearInput = {
  /** The order being evaluated, or null when there's no order. */
  order: {
    status: OrderStatus;
    /** menuCode of each cart line. */
    lineMenuCodes: string[];
  } | null;
  /** THIS order type's menus (with availability), as returned for the site. */
  menus: MenuShort[];
  /** The requested ready time; null/undefined (ASAP) is evaluated as "now". */
  requestedReadyTime?: string | null;
  /** The site's time zone, for evaluating scheduled-menu windows. */
  timeZone?: string;
};

export type OrderClearReason =
  | "none"
  | "not-draft"
  | "menu-mismatch"
  | "menu-closed";

/** The reason the order would be cleared (or "none"). */
export function orderClearReason({
  order,
  menus,
  requestedReadyTime,
  timeZone,
}: OrderClearInput): OrderClearReason {
  if (!order) return "none";

  // A completed/sent order is no longer the working cart.
  if (order.status !== OrderStatus.DRAFT) return "not-draft";

  const byCode = new Map(menus.map((m) => [m.code, m]));
  for (const code of order.lineMenuCodes) {
    const menu = byCode.get(code);
    // A line whose menu isn't one of this order type's menus (e.g. an ONLINE
    // item carried into a CATERING visit) can't be ordered here.
    if (!menu) return "menu-mismatch";
    // A time-scheduled menu (one with availability windows) that isn't open at
    // the requested time can't be ordered then. Menus without windows are always
    // available. Mirrors the backend's per-menu placement check.
    if (
      menu.availability?.length &&
      !isMenuOpen(menu, requestedReadyTime, timeZone)
    ) {
      return "menu-closed";
    }
  }

  return "none";
}

/** Convenience boolean wrapper around {@link orderClearReason}. */
export function shouldClearOrder(input: OrderClearInput): boolean {
  return orderClearReason(input) !== "none";
}
